import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

type AutomationStatus = {
  id: string;
  user_id: string;
  automation_type: string;
  status: 'active' | 'paused' | 'error' | 'disconnected';
  last_run_at?: string | null;
  next_run_at?: string | null;
  success_count: number;
  error_count: number;
  last_error?: string | null;
  configuration: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export function useAutomationStatus() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const automationQuery = useQuery<AutomationStatus[]>({
    queryKey: ["automation-status", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase.from as any)("automation_status")
        .select("*")
        .eq("user_id", user.id)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AutomationStatus[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const updateStatus = useMutation({
    mutationFn: async ({ 
      automation_type, 
      status, 
      last_error 
    }: { 
      automation_type: string; 
      status: AutomationStatus['status']; 
      last_error?: string | null;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };
      
      if (status === 'active') {
        updateData.last_run_at = new Date().toISOString();
        updateData.success_count = (supabase.from as any)("automation_status")
          .select("success_count")
          .eq("user_id", user.id)
          .eq("automation_type", automation_type)
          .single()
          .then((result: any) => (result.data?.success_count || 0) + 1);
      }
      
      if (last_error) {
        updateData.last_error = last_error;
        updateData.error_count = (supabase.from as any)("automation_status")
          .select("error_count")
          .eq("user_id", user.id)
          .eq("automation_type", automation_type)
          .single()
          .then((result: any) => (result.data?.error_count || 0) + 1);
      } else if (status === 'active') {
        updateData.last_error = null;
      }

      const { error } = await (supabase.from as any)("automation_status")
        .update(updateData)
        .eq("user_id", user.id)
        .eq("automation_type", automation_type);
      
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automation-status"] });
    },
    meta: {
      onError: (error: any) => console.error('Update automation status failed:', error),
    },
  });

  const gmailAutomation = automationQuery.data?.find(a => a.automation_type === 'gmail_processing');

  return {
    automations: automationQuery.data || [],
    gmailAutomation,
    isLoading: automationQuery.isLoading,
    error: automationQuery.error,
    updateStatus: updateStatus.mutateAsync,
    isUpdating: updateStatus.isPending,
  };
}